import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';

import { Typography } from '@mui/material';
import { extractStatusCode } from 'common/services/api/apiHelpers';
import { handleDeleteDialogError } from 'common/utils/errorHandlers';
import { useSnackbar } from 'notistack';

import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { DeleteDialog } from '../../common/components/deleteDialog/DeleteDialog';
import { setDialogOpen } from '../../common/components/deleteDialog/deleteDialogSlice';
import { selectPageInfo, setRows } from '../../common/components/PalmDataGrid/palmDataGridSlice';
import { deleteClaim } from '../../common/services/api/Claims/Claims.thunk';
import { DeleteDialogContentProps } from '../../common/types/TypeDefs';

export function ClaimDeleteDialogContent({
    title,
    itemId,
    editingRow,
}: Readonly<DeleteDialogContentProps<{ name: string, claimDataTypeName: string }>>) {
    const { name, claimDataTypeName } = editingRow ?? {};
    const { enqueueSnackbar } = useSnackbar();
    const [processing, setProcessing] = useState<boolean>(false);
    const pageInfo = useAppSelector(selectPageInfo);

    const successHandler = (): void => {
        dispatch(setDialogOpen(false));
        enqueueSnackbar('Claim has been deleted.', { variant: 'success' });
    };

    const dispatch = useAppDispatch();
    const params = useParams();
    const tenantId = params.tenantId;

    const claimDeleteCallback = useCallback(() => {
        if (itemId) {
            setProcessing(true);
            dispatch(deleteClaim({
                tenantId: tenantId ?? '',
                id: itemId.toString()
            }))
                .unwrap()
                .then((result) => {
                    dispatch(setRows(result.rows));
                    successHandler();
                    setProcessing(false);
                })
                .catch((error) => {
                    handleDeleteDialogError('Claim', enqueueSnackbar, extractStatusCode(error.message));
                    setProcessing(false);
                });
        }
    }, [itemId]);

    return  (
        <DeleteDialog deleteCallback={claimDeleteCallback} processing={processing ?? false} title={title}>
            <Typography color="info.light" fontSize='0.9rem' fontWeight='550'>
                Claim Name: <Typography component="span" color="text.primary" fontWeight="400">{name}</Typography>
            </Typography>
            <Typography color="info.light" fontSize='0.9rem' fontWeight='550'>
                Claim Type: <Typography component="span" color="text.primary" fontWeight="400">{claimDataTypeName}</Typography>
            </Typography>
            <Typography color="info.light" fontSize='0.9rem' fontWeight='550'>
                Tenant Name: <Typography component="span" color="text.primary" fontWeight="400">{pageInfo?.name}</Typography>
            </Typography>
        </DeleteDialog>
    );
}
